import type { GatewayBrowserClient } from "../gateway.ts";

export type InvoiceEntry = {
  id: string;
  companyId: string;
  invoiceNumber: string;
  periodStart: string;
  periodEnd: string;
  subtotalCents: number;
  vatRate: number;
  vatCents: number;
  totalCents: number;
  isReverseCharge: boolean;
  currency: string;
  status: string;
  pdfPath: string | null;
  createdAt: string;
};

export type SubscriptionEntry = {
  id: string;
  companyId: string;
  planId: string;
  status: string;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  plan?: {
    id: string;
    name: string;
    maintenanceFeeCents: number;
    includedTokens: number;
    overagePerMillionCents: number;
    isSubscription: boolean;
  };
};

export type BillingState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  billingLoading: boolean;
  billingError: string | null;
  billingSubscription: SubscriptionEntry | null;
  billingInvoices: InvoiceEntry[];
  billingSetupIntentSecret: string | null;
  billingSaving: boolean;
};

export async function loadBillingSummary(state: BillingState, companyId: string) {
  if (!state.client || !state.connected) {
    return;
  }
  if (state.billingLoading) {
    return;
  }
  state.billingLoading = true;
  state.billingError = null;
  try {
    const [subRes, invRes] = await Promise.all([
      state.client.request<{ ok: boolean; subscription: SubscriptionEntry | null; error?: string }>(
        "igloobot.billing.getSubscription",
        { companyId },
      ),
      state.client.request<{ ok: boolean; invoices: InvoiceEntry[]; error?: string }>(
        "igloobot.billing.invoices.list",
        { companyId },
      ),
    ]);
    if (subRes.ok) {
      state.billingSubscription = subRes.subscription;
    }
    if (invRes.ok) {
      state.billingInvoices = invRes.invoices;
    }
  } catch (err) {
    state.billingError = String(err);
  } finally {
    state.billingLoading = false;
  }
}

export async function downloadInvoice(
  state: BillingState,
  invoiceId: string,
): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  try {
    const res = await state.client.request<{ ok: boolean; pdf?: string; error?: string }>(
      "igloobot.billing.invoices.download",
      { invoiceId },
    );
    if (res.ok && res.pdf) {
      const binary = atob(res.pdf);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  } catch (err) {
    state.billingError = String(err);
  }
}
